import * as React from "react";
import { useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Input,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { Dismiss24Regular, Search20Regular } from "@fluentui/react-icons";
import { acquireGraphTokenSilent } from "../auth/authService";
import { getContacts, Contact } from "./graphContacts";
import { useTranslation } from "react-i18next";

interface ContactsPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (phone: string) => void;
  /**
   * The Graph token the signed-in session already holds. Preferred over a fresh
   * silent acquisition, which fails when the browser gives the add-in iframe
   * ephemeral storage — the session is valid, there is just nothing cached to
   * re-read.
   */
  graphToken?: string;
}

const useStyles = makeStyles({
  surface: { maxWidth: "92vw", width: "460px" },
  content: { display: "flex", flexDirection: "column", gap: "10px", maxHeight: "60vh" },
  list: { display: "flex", flexDirection: "column", gap: "6px", overflowY: "auto" },
  contact: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "6px 8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  name: { fontWeight: tokens.fontWeightSemibold },
  phones: { display: "flex", flexDirection: "column", gap: "2px" },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  state: { color: tokens.colorNeutralForeground3, textAlign: "center", padding: "16px" },
  error: { color: tokens.colorPaletteRedForeground1, textAlign: "center", padding: "16px" },
});

const ContactsPickerDialog: React.FC<ContactsPickerDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
  graphToken,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (phone: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(phone);
      else next.delete(phone);
      return next;
    });

  const addSelected = () => {
    selected.forEach((p) => onSelect(p));
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const token = graphToken ?? (await acquireGraphTokenSilent());
        if (!token) throw new Error(t("contacts.notSignedIn"));
        const list = await getContacts(token, search);
        if (!cancelled) setContacts(list);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timer = setTimeout(run, search ? 300 : 0); // debounce searches
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, search, graphToken]);

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss24Regular />}
                aria-label={t("common.close")}
                onClick={() => onOpenChange(false)}
              />
            }
          >
            {t("contacts.title")}
          </DialogTitle>
          <DialogContent className={styles.content}>
            <Input
              value={search}
              onChange={(_, d) => setSearch(d.value)}
              placeholder={t("contacts.searchPlaceholder")}
              contentBefore={<Search20Regular />}
            />
            {loading ? (
              <div className={styles.state}>
                <Spinner size="small" label={t("contacts.loading")} />
              </div>
            ) : error ? (
              <Text className={styles.error}>{error}</Text>
            ) : contacts.length === 0 ? (
              <Text className={styles.state}>{t("contacts.empty")}</Text>
            ) : (
              <div className={styles.list}>
                <Text className={styles.hint}>{t("contacts.hint")}</Text>
                {contacts.map((c) => (
                  <div key={c.id} className={styles.contact}>
                    <Text className={styles.name}>{c.name}</Text>
                    <div className={styles.phones}>
                      {c.phones.map((p) => (
                        <Checkbox
                          key={p}
                          label={p}
                          checked={selected.has(p)}
                          onChange={(_, d) => toggle(p, !!d.checked)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" disabled={selected.size === 0} onClick={addSelected}>
              {selected.size > 0
                ? t("contacts.addCount", { count: selected.size })
                : t("contacts.add")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ContactsPickerDialog;
